// Sidebar — Phase 1 chrome (white surface, ember text, hairline border)
// extended with the Phase 4 icon-rail responsive state.
//
// Three states (per VISUAL_REFACTOR_PLAN.md decision #1):
//   • lg+   (≥1024px) → full 220px width, wordmark + labels + role switcher
//   • md–lg (768–1023px) → 64px icon-rail, mark only, no labels, no role switcher
//   • <md   (<768px) → hidden by AppShell (BottomNav takes over)
//
// The Sidebar component itself owns the rail/full breakpoint; AppShell owns
// the rail/hidden one via `hidden md:flex`.
import { NavLink } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  Banknote,
  Calendar,
  ChefHat,
  Coins,
  Compass,
  FileCheck2,
  Flag,
  LayoutDashboard,
  Percent,
  Receipt,
  ScrollText,
  ShieldCheck,
  ShoppingBag,
  Ticket,
  User,
  Users,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useRoleStore } from '@/store/role.store'
import { useAuthStore } from '@/store/auth.store'
import type { UserRole } from '@/types/user.types'
import { isEnabled, type FeatureFlag } from '@/config/features'
import RoleSwitcher from './RoleSwitcher'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
  section?: string
  /** When set, the entry is hidden if this feature flag is OFF. */
  flag?: FeatureFlag
}

const NAV_ITEMS: Record<UserRole, NavItem[]> = {
  admin: [
    // Operations
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, section: 'Operations' },
    { to: '/admin/users', label: 'Users', icon: Users, section: 'Operations' },
    { to: '/admin/events', label: 'Events', icon: Calendar, section: 'Operations' },
    { to: '/admin/kyc', label: 'KYC', icon: FileCheck2, section: 'Operations' },
    { to: '/admin/disputes', label: 'Disputes', icon: Flag, section: 'Operations' },
    // Finance
    { to: '/admin/revenue', label: 'Revenue', icon: Receipt, section: 'Finance' },
    { to: '/admin/payouts', label: 'Payouts', icon: Banknote, section: 'Finance' },
    { to: '/admin/cpr', label: 'CPR Pool', icon: Coins, section: 'Finance' },
    { to: '/admin/fees', label: 'Fee Config', icon: Percent, section: 'Finance' },
    // Platform
    { to: '/admin/audit', label: 'Audit Log', icon: ScrollText, section: 'Platform' },
    { to: '/admin/admins', label: 'Admins', icon: ShieldCheck, section: 'Platform' },
  ],
  organiser: [
    { to: '/organiser', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/organiser/revenue', label: 'Revenue', icon: Receipt },
  ],
  vendor: [
    { to: '/vendor', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/vendor/menu', label: 'Menu', icon: ChefHat },
    { to: '/vendor/orders/live', label: 'Live Orders', icon: ShoppingBag, flag: 'VENDOR_ORDER_BOARD' },
    { to: '/vendor/earnings', label: 'Earnings', icon: Wallet, flag: 'VENDOR_EARNINGS_DASHBOARD' },
  ],
  attendee: [
    { to: '/events', label: 'Discover', icon: Compass },
    { to: '/tickets', label: 'Tickets', icon: Ticket },
    { to: '/wallet', label: 'Wallet', icon: Wallet, flag: 'ATTENDEE_WALLET' },
    { to: '/profile', label: 'Profile', icon: User },
  ],
}

// visibleItems filters a NavItem list by feature-flag state. Items without
// a `flag` are always visible; items with a `flag` only render when their
// flag is enabled.
function visibleItems(items: NavItem[]): NavItem[] {
  return items.filter((item) => !item.flag || isEnabled(item.flag))
}

export default function Sidebar() {
  const { activeRole } = useRoleStore()
  const user = useAuthStore((state) => state.user)
  const items = visibleItems(NAV_ITEMS[activeRole] ?? NAV_ITEMS.attendee)
  const isAdmin = activeRole === 'admin'

  // For admin, group items by section and render with headers.
  const renderAdminNav = () => {
    const sections: Record<string, NavItem[]> = {}
    for (const item of items) {
      const s = item.section ?? 'Other'
      if (!sections[s]) sections[s] = []
      sections[s].push(item)
    }
    return Object.entries(sections).map(([section, sectionItems]) => (
      <div key={section} className="mb-1">
        <div className="hidden lg:block px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] opacity-50">
          {section}
        </div>
        {sectionItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            title={item.label}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 rounded-lg text-sm font-semibold transition-colors',
                'justify-center lg:justify-start px-2 lg:px-3 py-2',
                isActive
                  ? 'bg-[var(--accent-light)] text-[var(--accent)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-muted)]',
              )
            }
          >
            <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
            <span className="hidden lg:inline">{item.label}</span>
          </NavLink>
        ))}
      </div>
    ))
  }

  return (
    <nav className="w-16 lg:w-[220px] h-full bg-[var(--surface)] border-r border-[var(--border-subtle)] flex flex-col">
      {/* Logo — mark only on rail, full wordmark on lg+ */}
      <div className="h-[54px] flex items-center justify-center lg:justify-start lg:px-5 border-b border-[var(--border-subtle)]">
        <img
          src="/logo-mark.svg"
          alt="Kaat"
          className="h-7 w-7 select-none lg:hidden"
          draggable={false}
        />
        <img
          src="/logo-wordmark.svg"
          alt="Kaat"
          className="hidden lg:block h-6 w-auto select-none"
          draggable={false}
        />
      </div>

      {/* Role switcher — full sidebar only (no room on the rail) */}
      <div className="hidden lg:block px-3 pt-4 pb-2">
        <RoleSwitcher />
      </div>

      {/* Navigation — role-aware */}
      <div className="flex-1 px-2 py-2 overflow-y-auto hide-scrollbar">
        {isAdmin ? renderAdminNav() : (
          <div className="space-y-0.5">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={item.label}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-lg text-sm font-semibold transition-colors',
                    'justify-center lg:justify-start px-2 lg:px-3 py-2',
                    isActive
                      ? 'bg-[var(--accent-light)] text-[var(--text-primary)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-muted)]',
                  )
                }
              >
                <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                <span className="hidden lg:inline">{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>

      {/* User footer — avatar only on rail, full identity on lg+ */}
      <div className="p-3 border-t border-[var(--border-subtle)]">
        <div className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[var(--surface-muted)] transition-colors cursor-default">
          <div className="w-7 h-7 rounded-full bg-[var(--accent)] flex items-center justify-center text-[10px] font-bold text-[var(--text-on-accent)] mx-auto lg:mx-0">
            {user?.firstName?.[0]?.toUpperCase() ?? 'K'}
          </div>
          <div className="hidden lg:block min-w-0 flex-1">
            <div className="text-xs font-bold text-[var(--text-primary)] truncate">
              {user ? `${user.firstName} ${user.lastName}` : 'Kaat User'}
            </div>
            <div className="text-[10px] text-[var(--text-muted)] capitalize">{activeRole}</div>
          </div>
        </div>
      </div>
    </nav>
  )
}
